import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from '@mui/material/styles';

// Context
import { AccountContext } from "@context/AccountProvider";
import { AlertContext } from "@context/AlertProvider";

// Components
import SmartForm from '@component/smartForm/SmartForm';
import SmartTextInput from '@component/smartForm/SmartInputText';
import SmartImageUpload from '@component/smartForm/SmartImageUpload';
import SmartContainer from '@component/smartForm/SmartContainer';
import SmartSwitchInput from "@component/smartForm/SmartSwitchInput";
import SmartDateInput from "@component/smartForm/SmartDateInput";

// Icons
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StarsIcon from '@mui/icons-material/Stars';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

// Mutations & Queries
import { useCreateShopItemMutation } from "@mutation/shopMutation";
import { useClubEventAllQuery } from "@query/clubQuery";

// Utils & Services
import { removeEmptyValues } from '@utils/generalUtils';
import { createLengthInRangeValidator, createLengthMaxValidator, createNumberInRangeValidator, notEmptyValidator } from "@component/smartForm/fieldValidators";
import { mapRewardedActivityEvent, RewardedActivityEvents } from "@service/rewardedActivityService";

function ShopItemCreateForm({ onSuccess }) {
    const { club } = useContext(AccountContext);
    const { addAlertBanner } = useContext(AlertContext);
    const { t } = useTranslation(["main", "shop"]);
    const theme = useTheme();

    const createShopItemMutation = useCreateShopItemMutation(club?.uuid);
    const clubEventAllQuery = useClubEventAllQuery(club.uuid);

    // Initial state for a new shop item
    const [shopItemModel, setShopItemModel] = useState({
        name: '',
        description: '',
        file_image_uuid: null,
        price_in_currency: null,
        price_in_points: null,
        max_available_quantity: null,
        is_global_available: true,
        available_from: new Date().toISOString(),
        available_to: null,
    });
    const [clubEvents, setClubEvents] = useState([]);

    // Field validators
    const nameValidator = createLengthInRangeValidator(3, 50);
    const descValidator = createLengthMaxValidator(500);
    const priceCurrencyValidator = createNumberInRangeValidator(1, 1000000);
    const pricePointsValidator = createNumberInRangeValidator(1, 10000000);
    const quantityValidator = createNumberInRangeValidator(1, 1000000);

    useEffect(() => {
        if (clubEventAllQuery.data?.data) {
            setClubEvents(clubEventAllQuery.data.data);
        }
    }, [clubEventAllQuery.data]);

    const handleSave = (newModel) => {
        const cleaned = removeEmptyValues({
            clubUuid: club.uuid,
            name: newModel.name,
            description: newModel.description,
            file_image_uuid: newModel.file_image_uuid,
            price_in_currency: Number(newModel.price_in_currency) || null,
            price_in_points: Number(newModel.price_in_points) || null,
            max_available_quantity: Number(newModel.max_available_quantity) || null,
            is_global_available: newModel.is_global_available,
            available_from: newModel.available_from,
            available_to: newModel.available_to,
        });

        createShopItemMutation.mutate({ data: cleaned}, {
            onSuccess: (data) => {
            addAlertBanner(t("main:saveSuccess"), "success");
            if (onSuccess){
                console.log(data)   
                onSuccess(data.data.uuid)
                setShopItemModel({
                    name: '',
                    description: '',
                    file_image_uuid: null,
                    price_in_currency: null,
                    price_in_points: null,
                    max_available_quantity: null,
                    is_global_available: true,
                    available_from: new Date().toISOString(),
                    available_to: null,
                })
            };
            },
            onError: () => addAlertBanner(t("main:saveError"), "error"),
        });
    };

    function getClubEventsVsProductPointPriceHelperText(clubEvents, pointValue) {
        if (!clubEvents || clubEvents.length === 0 || !pointValue) return "";
        var textRows = [];
        clubEvents.forEach(clubEvent => {
            const passedRewardedActivity = mapRewardedActivityEvent(clubEvent.event.name);
            if (!passedRewardedActivity || passedRewardedActivity.name === RewardedActivityEvents.WRITE_REVIEW.name) return;
            const eventCountForPointsEarned = Math.ceil(pointValue / clubEvent.reward);
            textRows.push(
                t(`shop:priceInPointsHelper.${passedRewardedActivity.translationKey}`, { "count": eventCountForPointsEarned })
            );
        });
        return t("shop:priceInPointsHelper.intro", { "points": pointValue }) + (textRows.length > 0 ? ` ${textRows.join(` ${t('main:or')} `)}` : "");
    }

    return (
        <SmartForm
            formModel={shopItemModel}
            setFormModel={setShopItemModel}
            onSave={handleSave}
            isCreateOnly={true}
            isLoading={createShopItemMutation.isPending}
            contentSx={{ gap: 2.5 }}
            isEdit={true} // Form is always editable for creation
            buttonPanelSx={{
                top: "10px",
                right: "10px",
            }}
        >
            <SmartContainer
                contentSx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 2.5, p: 0 }}
                sx={{ width: "100%" }}>

                <SmartImageUpload
                    imageSx={{
                        width: "auto",
                        height: "100%",
                        objectFit: "contain",
                        backgroundColor: "#fff"
                    }}
                    field="file_image_uuid" />

                <SmartContainer contentSx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 2.5, px: 2 }}>
                    <SmartTextInput field="name" valueSx={{ fontSize: theme.typography.h5 }} label={t("shop:productName")} validator={nameValidator} />

                    <SmartContainer contentSx={{ display: "flex", flexDirection: "row", p: 0, gap: 1, borderRadius: 1 }}>
                        <SmartTextInput
                            sx={{ flex: 1 }}
                            valueSx={{ fontSize: theme.typography.subtitle1 }}
                            field="price_in_points"
                            startIcon={<StarsIcon />}
                            label={t("shop:priceInPoints")}
                            type="number"
                            helperText={getClubEventsVsProductPointPriceHelperText(clubEvents, shopItemModel?.price_in_points)}
                            validator={pricePointsValidator} />

                        <SmartTextInput
                            sx={{ flex: 1 }}
                            valueSx={{ fontSize: theme.typography.subtitle1 }}
                            field="price_in_currency"
                            helperText={t("shop:pointsInCurrencyHelper")}
                            startIcon={<MonetizationOnIcon />}
                            label={t("shop:price")} type="number" validator={priceCurrencyValidator} />
                    </SmartContainer>

                    <SmartTextInput field="description" label={t("shop:description")} multiline minRows={4} validator={descValidator} />

                    <SmartContainer contentSx={{ display: "flex", flexDirection: "row", p: 0, gap: 1, borderRadius: 1 }}>
                        <SmartDateInput sx={{ flex: 1 }} startIcon={<CalendarMonthIcon />} field="available_from" label={t("shop:availableFrom")} validator={notEmptyValidator} />
                        <SmartDateInput sx={{ flex: 1 }} startIcon={<CalendarMonthIcon />} field="available_to" label={t("shop:availableTo")} validator={notEmptyValidator}/>
                    </SmartContainer>

                    <SmartContainer contentSx={{ display: "flex", flexDirection: "row", p: 0, gap: 1, borderRadius: 1 }}>
                        <SmartTextInput
                            sx={{ flex: 1 }}
                            field="max_available_quantity"
                            label={t("shop:maxAvailable")}
                            type="number"
                            validator={quantityValidator}
                            helperText={t("shop:availableQuentityHelperText")} />
                        <SmartSwitchInput
                            helperText={t("shop:manualAvailibilityHelperText")}
                            sx={{ flex: 1 }}
                            field="is_global_available"
                            label={t("shop:availability")}
                            trueValueLabel={t("shop:available")}
                            falseValueLabel={t("shop:unavailable")} />
                    </SmartContainer>
                </SmartContainer>
            </SmartContainer>
        </SmartForm >
    );
}

export default ShopItemCreateForm;